import React, { useState, useEffect } from 'react';
import { X, User, Phone, Briefcase, Building2, MapPin, Shield, Mail, CheckCircle2, AlertCircle, RefreshCw, Settings2, Smartphone, Copy, Check } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../services/api';

const SettingsModal = ({ user, onClose }) => {
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mfaData, setMfaData] = useState(null);
    const [mfaVerifyCode, setMfaVerifyCode] = useState('');
    const [isMfaLoading, setIsMfaLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [recoveryCodesData, setRecoveryCodesData] = useState(null);
    const [copiedCode, setCopiedCode] = useState(null);


    const fetchProfile = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiFetch('/auth/me');
            if (response.ok) {
                const data = await response.json();
                setProfile(data);
            } else {
                setError("Failed to connect to account service");
            }
        } catch (err) {
            setError("Connection Error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSetupMfa = async () => {
        setIsMfaLoading(true);
        try {
            const res = await apiFetch('/auth/2fa/setup', { method: 'POST' });
            const data = await res.json();
            console.log("MFA Setup Data Received:", data);
            setMfaData(data);
        } catch (err) {
            alert("Failed to initialize 2FA");
        } finally {
            setIsMfaLoading(false);
        }
    };

    const handleVerifyMfa = async () => {
        setIsMfaLoading(true);
        try {
            const res = await apiFetch('/auth/2fa/verify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: mfaVerifyCode })
            });
            const data = await res.json();
            if (data.success) {
                setProfile({ ...profile, twoFactorEnabled: true });
                setMfaData(null);
                setMfaVerifyCode('');
                fetchProfile(); // Refresh profile to be sure
            } else {
                alert(data.message);
            }
        } catch (err) {
            alert("Verification failed");
        } finally {
            setIsMfaLoading(false);
        }
    };

    const handleDisableMfa = async () => {
        if (!window.confirm("Are you sure you want to disable 2FA? This will make your account less secure.")) return;
        setIsMfaLoading(true);
        try {
            const res = await apiFetch('/auth/2fa/disable', { method: 'POST' });
            if (res.ok) {
                setProfile({ ...profile, twoFactorEnabled: false });
                fetchProfile();
            }
        } catch (err) {
            alert("Failed to disable 2FA");
        } finally {
            setIsMfaLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleGenerateRecoveryCodes = async () => {
        if (profile.recoveryCodesCount > 0 && !window.confirm('This will invalidate your existing recovery codes. Generate new ones?')) return;
        setIsMfaLoading(true);
        try {
            const res = await apiFetch('/auth/2fa/recovery-codes/generate', { method: 'POST' });
            const data = await res.json();
            if (res.ok && data.codes) {
                setRecoveryCodesData(data.codes);
                fetchProfile();
            } else { alert('Failed to generate recovery codes'); }
        } catch { alert('Connection error'); } finally { setIsMfaLoading(false); }
    };

    const copySingleCode = (code, idx) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(idx);
        setTimeout(() => setCopiedCode(null), 1800);
    };

    useEffect(() => {
        fetchProfile();
    }, []);



    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-overlay"
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.4)',
                backdropFilter: 'blur(12px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                padding: '20px'
            }}
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 30 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 30 }}
                className="premium-card animate-premium-in"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    overflow: 'hidden',
                    position: 'relative',
                    background: '#FFFFFF'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Top Theme Accent Bar */}
                <div style={{ height: '4px', background: 'linear-gradient(90deg, #4169E1, #1e40af, #4169E1)', backgroundSize: '200% auto' }} />

                <header style={{ padding: '24px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', background: 'rgba(255,255,255,0.8)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="premium-icon-container" style={{ background: 'var(--primary-glow)', color: 'var(--primary)', width: '36px', height: '36px' }}>
                            <Shield size={18} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: '900', letterSpacing: '-0.5px', textTransform: 'uppercase', color: '#0f172a' }}>
                                Account Details
                            </h2>
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '2px' }}>
                                Your Profile Information
                            </p>
                        </div>
                    </div>

                    <button onClick={onClose} className="btn-ghost" style={{ padding: '10px', borderRadius: '14px', background: 'var(--bg-secondary)', border: '1px solid var(--border-glass)' }}>
                        <X size={20} className="text-slate-500" />
                    </button>
                </header>

                <div className="scrollbar-hide" style={{ padding: '32px', maxHeight: '75vh', overflowY: 'auto' }}>
                    <AnimatePresence mode="wait">
                        {isLoading ? (
                            <motion.div
                                key="loading"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                style={{ padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}
                            >
                                <div className="animate-pulse-soft" style={{ width: '80px', height: '80px', borderRadius: '28px', background: 'var(--primary-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Shield size={32} className="text-primary" />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <p style={{ fontSize: '0.9rem', fontWeight: '800', color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Loading Profile...</p>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '600', marginTop: '4px' }}>Getting your details</p>
                                </div>
                            </motion.div>
                        ) : error ? (
                            <motion.div
                                key="error"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                style={{ padding: '20px' }}
                            >
                                <div style={{ padding: '32px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '24px', textAlign: 'center' }}>
                                    <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: '#fee2e2', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                                        <AlertCircle size={28} />
                                    </div>
                                    <h3 style={{ fontSize: '1rem', fontWeight: '800', color: '#991b1b', textTransform: 'uppercase', marginBottom: '8px' }}>Connection Failed</h3>
                                    <p style={{ fontSize: '0.85rem', color: '#b91c1c', fontWeight: '500', marginBottom: '24px', lineHeight: '1.5' }}>{error}. We couldn't load your profile information at this time.</p>
                                    <button
                                        onClick={fetchProfile}
                                        className="btn-primary"
                                        style={{ margin: '0 auto', fontSize: '0.8rem', padding: '10px 20px' }}
                                    >
                                        <RefreshCw size={14} />
                                        <span>Try Again</span>
                                    </button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="content"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}
                            >
                                {/* Header Avatar Section */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '10px 0' }}>
                                    <div className="identity-avatar">
                                        {profile.fullName?.charAt(0) || profile.mail?.charAt(0).toUpperCase()}
                                    </div>
                                    <div style={{ textAlign: 'center', width: '100%' }}>
                                        <h3 style={{ fontSize: '1.75rem', fontWeight: '900', letterSpacing: '-0.02em', color: '#0f172a', textTransform: 'uppercase' }}>
                                            {profile.fullName || 'Profile Name Not Set'}
                                        </h3>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '6px' }}>
                                            <span className="enterprise-badge" style={{ background: 'var(--primary-glow)', color: '#4169E1', borderColor: 'rgba(65,105,225,0.2)', fontWeight: '800' }}>
                                                {profile.role} Access Level
                                            </span>
                                            {profile.twoFactorEnabled && (
                                                <span className="enterprise-badge" style={{ background: '#f0fdf4', color: '#166534', borderColor: '#dcfce7', fontWeight: '800' }}>
                                                    2FA ACTIVE
                                                </span>
                                            )}
                                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '700' }}>{profile.mail}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Information Matrix */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                    <ProfileCard
                                        icon={Briefcase}
                                        label="Designation"
                                        value={profile.designation}
                                    />
                                    <ProfileCard
                                        icon={Building2}
                                        label="Department"
                                        value={profile.department}
                                    />
                                    <ProfileCard
                                        icon={Mail}
                                        label="Email ID"
                                        value={profile.mail}
                                    />
                                    <ProfileCard
                                        icon={Phone}
                                        label="Phone Number"
                                        value={profile.mobile}
                                    />
                                </div>

                                {/* Address Block */}
                                <div className="address-hero">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', opacity: 0.6 }}>
                                        <MapPin size={16} />
                                        <span style={{ fontSize: '0.7rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.15em' }}>Your Address</span>
                                    </div>
                                    <p style={{ fontSize: '0.9rem', fontWeight: '500', lineHeight: '1.6', opacity: 0.9 }}>
                                        {profile.address || "No address provided."}
                                    </p>
                                </div>

                                {/* Security Block */}
                                <div style={{ padding: '24px', background: 'var(--bg-secondary)', borderRadius: '24px', border: '1px solid var(--border-glass)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--primary-glow)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Smartphone size={20} />
                                            </div>
                                            <div>
                                                <h4 style={{ fontSize: '0.95rem', fontWeight: '800', color: '#0f172a' }}>Two-Factor Auth</h4>
                                                <p style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--text-muted)' }}>Secure your mailbox</p>
                                            </div>
                                        </div>
                                        {profile.twoFactorEnabled ? (
                                            <button onClick={handleDisableMfa} disabled={isMfaLoading} className="btn-ghost" style={{ color: '#ef4444', background: '#fef2f2', border: '1px solid #fee2e2', padding: '8px 16px', fontSize: '0.75rem' }}>
                                                Disable
                                            </button>
                                        ) : !mfaData ? (
                                            <button onClick={handleSetupMfa} disabled={isMfaLoading} className="btn-primary" style={{ padding: '8px 16px', fontSize: '0.75rem' }}>
                                                Enable 2FA
                                            </button>
                                        ) : null}
                                    </div>

                                    {mfaData && (
                                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '20px', padding: '24px', background: '#fff', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', alignItems: 'center' }}>
                                                <div style={{ padding: '16px', background: '#fff', borderRadius: '16px', border: '1px solid #eee', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                                    <QRCodeCanvas
                                                        value={mfaData.otpAuthUrl}
                                                        size={180}
                                                        level={"M"}
                                                        includeMargin={true}
                                                    />
                                                </div>

                                                <div style={{ width: '100%' }}>
                                                    <h5 style={{ fontSize: '0.8rem', fontWeight: '800', color: 'var(--primary)', marginBottom: '12px', textTransform: 'uppercase', textAlign: 'center' }}>Setup Instructions</h5>

                                                    <div style={{ marginBottom: '20px' }}>
                                                        <p style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '8px', color: '#334155' }}>1. Scan with Authenticator App</p>
                                                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '12px' }}>Open Google Authenticator, Microsoft Authenticator or Authy and scan the QR code above.</p>
                                                    </div>

                                                    <div style={{ marginBottom: '20px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                            <span style={{ fontSize: '0.7rem', fontWeight: '900', color: '#64748b', textTransform: 'uppercase' }}>Manual Setup Key:</span>
                                                            <button onClick={() => copyToClipboard(mfaData.secret)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#22c55e' : 'var(--primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                {copied ? <Check size={14} /> : <Copy size={14} />}
                                                                <span style={{ fontSize: '0.7rem', fontWeight: '800' }}>{copied ? 'COPIED' : 'COPY'}</span>
                                                            </button>
                                                        </div>
                                                        <code style={{
                                                            fontSize: '0.95rem',
                                                            fontWeight: '900',
                                                            color: '#0f172a',
                                                            letterSpacing: '0.5px',
                                                            display: 'block',
                                                            textAlign: 'center',
                                                            background: '#fff',
                                                            padding: '12px',
                                                            borderRadius: '8px',
                                                            border: '1px solid #eee',
                                                            wordBreak: 'break-all',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            {mfaData.secret}
                                                        </code>
                                                    </div>

                                                    <div style={{ marginBottom: '20px' }}>
                                                        <p style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '12px', color: '#334155' }}>2. Enter 6-digit verification code:</p>
                                                        <div style={{ display: 'flex', gap: '12px' }}>
                                                            <input
                                                                type="text"
                                                                maxLength="6"
                                                                placeholder="000 000"
                                                                value={mfaVerifyCode}
                                                                onChange={e => setMfaVerifyCode(e.target.value.replace(/\D/g, ''))}
                                                                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '2px solid #e2e8f0', fontSize: '1.25rem', fontWeight: '800', textAlign: 'center', outline: 'none', transition: 'border-color 0.2s' }}
                                                                className="focus:border-primary"
                                                            />
                                                            <button onClick={handleVerifyMfa} disabled={mfaVerifyCode.length < 6 || isMfaLoading} className="btn-primary" style={{ padding: '0 24px', borderRadius: '12px', fontWeight: '800' }}>
                                                                Verify
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <button onClick={() => setMfaData(null)} style={{ width: '100%', padding: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#ef4444', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '10px', cursor: 'pointer' }}>Cancel Setup</button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    {!profile.twoFactorEnabled && !mfaData && (
                                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: '500', lineHeight: '1.4' }}>
                                            Protect your account from unauthorized access by requiring a verification code from your phone whenever you log in.
                                        </p>
                                    )}
                                </div>

                                {/* ── RECOVERY CODES (only shown when 2FA is active) ── */}
                                {profile.twoFactorEnabled && (
                                    <div style={{ borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>

                                        {/* Header bar */}
                                        <div style={{ padding: '18px 24px', background: 'linear-gradient(135deg, #1e3a8a, #1e40af)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.15)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <span style={{ fontSize: '1.1rem' }}>🔑</span>
                                                </div>
                                                <div>
                                                    <p style={{ color: 'white', fontSize: '0.88rem', fontWeight: '800', margin: 0 }}>Recovery Codes</p>
                                                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.7rem', margin: 0, marginTop: '2px' }}>
                                                        {profile.recoveryCodesCount > 0
                                                            ? `${profile.recoveryCodesCount} of 10 codes remaining`
                                                            : 'No codes generated yet — set up now'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button onClick={handleGenerateRecoveryCodes} disabled={isMfaLoading}
                                                style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700', cursor: 'pointer', backdropFilter: 'blur(8px)', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                                                onMouseOver={e => e.target.style.background = 'rgba(255,255,255,0.25)'}
                                                onMouseOut={e => e.target.style.background = 'rgba(255,255,255,0.15)'}>
                                                {isMfaLoading ? '⏳ ...' : profile.recoveryCodesCount > 0 ? '↺ Regenerate' : '✦ Generate'}
                                            </button>
                                        </div>

                                        <div style={{ padding: '20px 24px' }}>
                                            {!recoveryCodesData && profile.recoveryCodesCount === 0 && (
                                                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                                                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.6' }}>
                                                        Generate 10 one-time backup codes to access your account if you lose your phone.<br />
                                                        <strong style={{ color: '#475569' }}>Each code can only be used once.</strong>
                                                    </p>
                                                </div>
                                            )}

                                            {profile.recoveryCodesCount > 0 && !recoveryCodesData && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                                                    <span>✅</span>
                                                    <p style={{ fontSize: '0.78rem', color: '#16a34a', fontWeight: '600', margin: 0 }}>
                                                        You have {profile.recoveryCodesCount} backup code(s) saved. Use "Regenerate" to replace them.
                                                    </p>
                                                </div>
                                            )}

                                            {/* Newly generated codes display */}
                                            {recoveryCodesData && (
                                                <div>
                                                    <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', border: '1px solid #fde68a', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>⚠️</span>
                                                        <p style={{ fontSize: '0.77rem', color: '#92400e', fontWeight: '700', lineHeight: '1.5', margin: 0 }}>
                                                            Save these codes now! They will not be shown again. Each code is single-use.
                                                        </p>
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
                                                        {recoveryCodesData.map((code, idx) => (
                                                            <motion.div key={idx} onClick={() => copySingleCode(code, idx)}
                                                                whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                                                style={{ fontFamily: 'monospace', fontSize: '0.88rem', fontWeight: '800', color: copiedCode === idx ? '#166534' : '#1e40af', background: copiedCode === idx ? '#f0fdf4' : '#eff6ff', border: `1px solid ${copiedCode === idx ? '#bbf7d0' : '#bfdbfe'}`, borderRadius: '10px', padding: '10px 14px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s', userSelect: 'all', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                                {copiedCode === idx ? <><span>✓</span> Copied!</> : code}
                                                            </motion.div>
                                                        ))}
                                                    </div>

                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button onClick={() => navigator.clipboard.writeText(recoveryCodesData.join('\n'))}
                                                            style={{ flex: 1, padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#4169E1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                            📋 Copy All
                                                        </button>
                                                        <button onClick={() => {
                                                            const blob = new Blob([`XeMail Recovery Codes\nGenerated: ${new Date().toLocaleString()}\n\n${recoveryCodesData.join('\n')}\n\nKeep these safe. Each code is single-use.`], { type: 'text/plain' });
                                                            const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'xemail-recovery-codes.txt'; a.click();
                                                        }}
                                                            style={{ flex: 1, padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '0.75rem', fontWeight: '700', color: '#4169E1', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                            ⬇ Download .txt
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 24px', background: '#f0fdf4', color: '#166534', borderRadius: '16px', border: '1px solid #dcfce7' }}>
                                    <CheckCircle2 size={18} />
                                    <p style={{ fontSize: '0.75rem', fontWeight: '700' }}>Your account information is verified and secure.</p>
                                </div>
                            </motion.div>
                        )}

                    </AnimatePresence>
                </div>

                {/* Footer Status */}
                {!isLoading && !error && (
                    <footer style={{ padding: '24px 32px', background: 'var(--bg-secondary)', borderTop: '1px solid var(--border-glass)', display: 'flex', justifyContent: 'center', gap: '16px' }}>
                        <button
                            onClick={onClose}
                            style={{ fontSize: '0.7rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)' }}
                            className="btn-ghost"
                        >
                            Dismiss
                        </button>
                    </footer>
                )}
            </motion.div>
        </motion.div>
    );
};

const ProfileCard = ({ icon: Icon, label, value }) => (
    <div className="info-card" style={{ border: '1px solid var(--border-glass)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            <Icon size={14} />
            <span style={{ fontSize: '0.65rem', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}</span>
        </div>
        <p style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {value || 'Not provided'}
        </p>
    </div>
);

export default SettingsModal;
